import React, { useState } from 'react';
import { Edit2, Save, X } from 'lucide-react';
import './PricingTiersCard.css';

const PricingTiersCard = ({ tiers, onUpdate }) => {
  const [editingTier, setEditingTier] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [updateReason, setUpdateReason] = useState('');

  // Only flat and linear formulas are editable
  const isEditable = (tier) => ['flat', 'linear', 'mixed'].includes(tier.formula_type);

  const startEdit = (tier) => {
    if (!isEditable(tier)) return;
    setEditingTier(tier.id);
    setEditValues({
      base_amount: tier.base_amount,
      per_acre_rate: tier.per_acre_rate
    });
    setUpdateReason('');
  };

  const cancelEdit = () => {
    setEditingTier(null);
    setEditValues({});
    setUpdateReason('');
  };

  const saveEdit = async () => {
    if (!updateReason.trim()) {
      alert('Please provide a reason for this pricing change');
      return;
    }

    try {
      await onUpdate(editingTier, editValues, updateReason);
      cancelEdit();
    } catch (error) {
      console.error('Error saving tier:', error);
      alert('Failed to save pricing tier');
    }
  };

  const calculateExample = (tier, acres) => {
    let cost = 0;

    switch (tier.formula_type) {
      case 'flat':
        cost = tier.base_amount;
        break;
      case 'linear':
      case 'mixed':
        cost = tier.base_amount + (tier.per_acre_rate * acres);
        break;
      case 'power':
        cost = tier.power_coefficient * Math.pow(acres, tier.power_exponent) + tier.constant;
        break;
      default:
        cost = 0;
        break;
    }

    return Math.ceil(cost / 10) * 10;
  };

  const formatSizeRange = (tier) => {
    if (tier.size_max >= 150) return `${tier.size_min}+ acres`;
    return `${tier.size_min} - ${tier.size_max} acres`;
  };

  const getExampleAcres = (tier) => {
    if (tier.formula_type === 'flat') return tier.size_min || 0.25;
    if (tier.size_max >= 150) return 20;
    return (tier.size_min + tier.size_max) / 2;
  };

  return (
    <div className="pricing-tiers-table">
      <div className="tiers-header">
        <div className="tier-col size">Size Range</div>
        <div className="tier-col formula">Pricing</div>
        <div className="tier-col example">Example</div>
        <div className="tier-col actions"></div>
      </div>

      {tiers.map((tier) => {
        const isEditing = editingTier === tier.id;
        const exampleAcres = getExampleAcres(tier);
        const examplePrice = calculateExample(tier, exampleAcres);
        const canEdit = isEditable(tier);

        return (
          <div key={tier.id} className={`tier-row ${isEditing ? 'editing' : ''}`}>
            {isEditing ? (
              <div className="tier-edit-form">
                <div className="edit-header">
                  <span className="edit-title">Editing: {tier.name}</span>
                  <span className="edit-range">{formatSizeRange(tier)}</span>
                </div>

                <div className="edit-fields">
                  {tier.formula_type === 'flat' && (
                    <div className="edit-field">
                      <label>Flat Rate</label>
                      <div className="input-with-prefix">
                        <span className="prefix">$</span>
                        <input
                          type="number"
                          value={editValues.base_amount || ''}
                          onChange={(e) => setEditValues({...editValues, base_amount: parseFloat(e.target.value)})}
                          step="10"
                        />
                      </div>
                    </div>
                  )}

                  {(tier.formula_type === 'linear' || tier.formula_type === 'mixed') && (
                    <>
                      <div className="edit-field">
                        <label>Base Amount</label>
                        <div className="input-with-prefix">
                          <span className="prefix">$</span>
                          <input
                            type="number"
                            value={editValues.base_amount || ''}
                            onChange={(e) => setEditValues({...editValues, base_amount: parseFloat(e.target.value)})}
                            step="10"
                          />
                        </div>
                      </div>
                      <div className="edit-field">
                        <label>Per Acre Rate</label>
                        <div className="input-with-prefix">
                          <span className="prefix">$</span>
                          <input
                            type="number"
                            value={editValues.per_acre_rate || ''}
                            onChange={(e) => setEditValues({...editValues, per_acre_rate: parseFloat(e.target.value)})}
                            step="10"
                          />
                          <span className="suffix">/acre</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="edit-reason">
                  <label>Reason for change <span className="required">*</span></label>
                  <input
                    type="text"
                    value={updateReason}
                    onChange={(e) => setUpdateReason(e.target.value)}
                    placeholder="e.g., Annual price adjustment"
                  />
                </div>

                <div className="edit-actions">
                  <button className="edit-btn save" onClick={saveEdit}>
                    <Save size={14} />
                    Save
                  </button>
                  <button className="edit-btn cancel" onClick={cancelEdit}>
                    <X size={14} />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="tier-col size">
                  <span className="size-range">{formatSizeRange(tier)}</span>
                </div>
                <div className="tier-col formula">
                  {tier.formula_type === 'flat' && (
                    <span className="formula-display">
                      <strong>${tier.base_amount?.toLocaleString()}</strong> flat
                    </span>
                  )}
                  {(tier.formula_type === 'linear' || tier.formula_type === 'mixed') && (
                    <span className="formula-display">
                      <strong>${tier.base_amount?.toLocaleString()}</strong> + <strong>${tier.per_acre_rate?.toLocaleString()}</strong>/acre
                    </span>
                  )}
                  {tier.formula_type === 'power' && (
                    <span className="formula-display formula-locked">
                      Custom formula
                    </span>
                  )}
                </div>
                <div className="tier-col example">
                  <span className="example-calc">
                    {exampleAcres} acres = <strong>${examplePrice.toLocaleString()}</strong>
                  </span>
                </div>
                <div className="tier-col actions">
                  {canEdit && (
                    <button
                      className="tier-edit-btn"
                      onClick={() => startEdit(tier)}
                      title="Edit this tier"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PricingTiersCard;
